import { useState, useEffect, useRef } from "react";
import { BrowserRouter, Routes, Route, Link, useNavigate, useLocation } from "react-router-dom";
import { Mic, Mic2, FileText, Layers, Share2, Camera, ChevronLeft, ChevronRight, ArrowRight, Zap, Mail, HelpCircle, Activity, Sparkles, ShieldCheck } from "lucide-react";

const SAGE = "#778979";
const SAGE_DARK = "#556A5C";
const SAGE_LIGHT = "#f0f4f1";
const APP_STORE_URL = "https://apps.apple.com/app/id6761543461";

const translations = {
  et: {
    nav: ["Funktsioonid", "Töövoog", "Hinnastus", "Kontakt"],
    navPaths: ["/funktsioonid", "/toovoog", "/hinnastus", "/kontakt"],
    badge: "Dokumentaalfilm · Välitöö",
    heroTitle: "Intervjuu lõpeb.\nKatteplaanid algavad.",
    heroSub: "DOKUField muudab iga intervjuu koheseks tegevuskavaks — transkriptsioon, analüüs ja filmitavad kaadrid otse võtteplatsil.",
    ctaPrimary: "Hangi äpp",
    ctaSub: "iPhone & iPad · API võti vajalik",
    featTitle: "Kõik mida välitöödel vajad",
    featSub: "DOKUField on loodud videograafidele ja sisuloojatele kes tahavad rohkem aega pühendada loomingule, mitte administreerimisele.",
    features: [
      { title: "Salvestus & transkript", desc: "Vajuta REC — DOKUField teeb ülejäänu. Intervjuu transkribeeritakse automaatselt eesti ja inglise keeles ning helifail (.m4a) salvestatakse telefoni, et saaksid ReviewScreen'il tagasi mängida ja lõike enne eksporti kontrollida.", icon: Mic },
      { title: "Töökindel salvestus", desc: "Salvestis ja transkript ei kao ka tõrke korral. Välitingimustes võid kindel olla, et materjal püsib alles.", icon: ShieldCheck },
      { title: "Katteplaanide AI", desc: "AI genereerib 4 kategoorias konkreetsed filmitavad kaadrid — SIIN RUUMIS, LÄHIÜMBRUS, SÜMBOOLSED, ÜLDPLAAN.", icon: Layers },
      { title: "AI lühianalüüs", desc: "3 tugevaimat tsitaati, cold open moment ja emotsionaalne tipp — vahetult pärast intervjuud.", icon: FileText },
      { title: "Asukoha fotod", desc: "Lisa fotod intervjuu setist — AI näeb ruumi ja soovitab täpsemaid visuaale.", icon: Camera },
      { title: "Ekspordi menüü", desc: "Kolm nuppu on asendatud ühega. Vali transkript, analüüs, helifail või kõik koos — üks AirDrop sessioon.", icon: Share2 },
      { title: "Privaatsus & kontroll", desc: "Heli saadetakse transkribeerimiseks AI teenusesse. Transkript jääb sinu seadmesse.", icon: Zap },
      { title: "Väline mikrofon töötab automaatselt", desc: "Ühenda Bluetooth-mikrofon või lavalier — DOKUField tuvastab selle ise ja kuvab indikaatori, milline sisend on aktiivne.", icon: Mic2 },
      { title: "Reaalajas helitase", desc: "Salvestuse ajal näed taseme meetrit (roheline / kollane / punane). Kohe selge, kas mikrofon püüab heli ja kas tase pole liiga vali.", icon: Activity },
      { title: "7-päevane tasuta proov", desc: "Proovi katteplaane ja lühianalüüsi tasuta. Tühista igal ajal Apple ID seadetest. Pärast prooviperioodi €19.99/kuu või €149.99/aasta.", icon: Sparkles },
    ],
    workflowTitle: "Neli sammu võtteplatsil",
    workflowSub: "Äpp on loodud nii, et saad seda kasutada isegi kui sul on kiire — iga samm võtab minimaalselt aega.",
    steps: [
      { n: "01", title: "Sisesta info", desc: "Asukoht, teema, intervjueeritav. Lisa fotod setist." },
      { n: "02", title: "Salvesta", desc: "Vajuta REC. Telefon salvestab — transkript valmib pärast salvestust." },
      { n: "03", title: "Saa kaadrid", desc: "AI analüüsib ja soovitab filmitavaid kaadreid — sa oled veel kohal." },
      { n: "04", title: "Ekspordi transkript", desc: "Jaga .txt AirDropiga — transkript koos metaandmetega on kohe kasutamisvalmis." },
    ],
    workflowDetailed: [
      {
        n: "01", title: "Sisesta intervjuu info", time: "1 minut",
        simple: "Täida põhiandmed enne salvestust.",
        what: "Ava äpp ja täida väljad: Asukoht, Teema, Intervjueerija, Intervjueeritav. Soovi korral lisa kuni 3 fotot võtteplatsist — AI kasutab neid hiljem täpsemate katteplaanide soovitamiseks.",
        screen: "Näed lihtsat vormi. Kõik väljad on valikulised peale asukoha.",
        tip: "Teema väljale võid lisada pikema kirjelduse — mida rohkem konteksti, seda täpsemad katteplaanid.",
      },
      {
        n: "02", title: "Salvesta intervjuu", time: "Intervjuu pikkus + ~2 minutit töötlust",
        simple: "Vajuta REC ja alusta intervjuud.",
        what: "Telefon salvestab heli. Kui vajutad Lõpeta, saadab äpp helifaili automaatselt Whisper AI-le töötlemiseks.",
        screen: "Näed kellaaja loendust ja vilkuvat punast punkti. Pärast salvestust kuvatakse Whisperi töötlusaeg — transkript ilmub, kui Whisper lõpetab.",
        tip: "Telefon kasutab ainult transkriptsiooni jaoks sisseehitatud mikrofoni — sinu peamine audio salvestub eraldi seadmesse nagu tavaliselt.",
      },
      {
        n: "03", title: "Vaata üle ja genereeri katteplaanid", time: "~30 sekundit",
        simple: "Whisper on valmis — loe transkripti ja genereeri katteplaanid.",
        what: "Whisper tagastab täpse lõpliku transkripti. Vajuta 'Genereeri katteplaanid' — AI loeb transkripti ning soovitab konkreetseid filmitavaid kaadreid 4 kategoorias: SIIN RUUMIS, LÄHIÜMBRUS, SÜMBOOLSED, ÜLDPLAAN.",
        screen: "Näed Whisper transkripti koos sõnade arvuga. Pärast genereerimist avaneb katteplaanide ekraan. Vajuta kaardil — maha kriipsutus kui oled filminud.",
        tip: "Soovi korral genereeri ka Lühianalüüs — 3 tugevaimat tsitaati, cold open moment ja emotsionaalne tipp.",
      },
      {
        n: "04", title: "Ekspordi transkript", time: "10 sekundit",
        simple: "Jaga transkript AirDropiga — kõik on kasutamisvalmis.",
        what: "Vajuta 'Jaga .txt' ja AirDrop Maci. Fail sisaldab automaatselt metaandmete päist (asukoht, teema, intervjueeritav, kuupäev) ning täielikku transkripti. Saad jagada ka HTML versiooni mis avaneb Safaris interaktiivse lehena.",
        screen: "iOS jagamise aken. Vali AirDrop, seejärel Mac — fail on kohe Downloadsides.",
        tip: "Transkript jääb alati Ajaloo vahekaardile. Saad naasta ja katteplaane märkida ka hiljem.",
      },
    ],
    testimonialTitle: "Dokumentalistide jaoks dokumentalistidelt",
    testimonials: [
      { quote: "Varem kaotasin pooled katteplaanide ideed enne kui jõudsin neid üles kirjutada. Nüüd on kõik kohe telefonis.", name: "Hannes Raimla", role: "Videograaf" },
      { quote: "Transkriptsioon tabab eestikeelset kõnet üllatavalt hästi. Transkript on valmis enne kui kaamerat lahti võtan.", name: "Mihkel", role: "Kaameraoperaator" },
    ],
    pricingTitle: "Lihtne hinnastus",
    pricingDesc: "Üks tellimus. Sinu oma API võtmed. Täielik kontroll kulude üle.",
    plans: [
      { name: "Solo", price: "€19.99", period: "/ kuu", yearPrice: "€149.99 / aasta", yearSaving: "Säästad 2 kuud", desc: "DOKUField kõigi funktsioonidega", features: ["Piiramatu arv intervjuusid", "Katteplaanid + lühianalüüs", "Asukoha fotode analüüs", "Transkripti eksport (.txt)", "iPhone + iPad äpp"], cta: "Alusta prooviperioodiga", highlight: false },
    ],
    apiExplain: {
      title: "Mis on API kulud?",
      desc: "DOKUField kasutab kahte AI teenust — kumbki katab nii transkriptsiooni kui analüüsi. Maksad otse teenusepakkujale, mitte meile.",
      whyTwo: "Miks kaks teenust? Kumbki katab nii transkriptsiooni kui analüüsi — vali üks või kasuta mõlemat Hübriid-režiimis (Whisperi täpne ajastus + Gemini tekst).",
      services: [
        { name: "OpenAI", desc: "Transkriptsioon (Whisper) + analüüs ja katteplaanid (GPT-4o). Toetab eesti keelt. Iseseisev võti — piisab üksi.", cost: "~€0.006 transkriptsioon (1h) + GPT-4o analüüs", link: "https://platform.openai.com", linkText: "Loo OpenAI konto →" },
        { name: "Google Gemini", desc: "Parem eestikeelne transkriptsioon koos kõneleja-siltidega + analüüs ja katteplaanid. Iseseisev võti — piisab üksi.", cost: "~€0.02–0.05 / intervjuu", link: "https://ai.google.dev", linkText: "Loo Google AI konto →" },
      ],
      total: "Hübriid-režiimis maksimaalselt ~€0.06 ühe tunnisel intervjuul (Whisper + Gemini). Ühe võtmega on kulu väiksem. Lühemad intervjuud maksavad vähem — Whisper arveldab audio pikkuse järgi.",
    },
    ctaTitle: "Alusta täna.",
    ctaDesc: "Saadaval App Store'is.",
    ctaBtn: "Hangi DOKUField",
    footer: "DOKUField · Välitöö tööriist videograafidele ja sisuloojatele",
    contactTitle: "Kontakt",
    contactDesc: "Küsimused, tagasiside või soov äppi proovida? Kirjuta meile.",
    contactEmail: "info@dokufield.app",
    faqTitle: "Korduma kippuvad küsimused",
    faqs: [
      { q: "Kas äpp töötab ilma internetita?", a: "Salvestamine töötab offline. AI transkriptsioon ja katteplaanide genereerimine vajavad internetiühendust." },
      { q: "Kas äpp töötab Androidil?", a: "Praegu ainult iOS. Android versioon on plaanis." },
      { q: "Kas transkriptsioon töötab eesti keeles?", a: "Jah — eesti ja inglise keeles. Whisper toetab mõlemat ja annab täpseid tulemusi." },
      { q: "Kas pean API võtmeid ise haldama?", a: "Jah — vaja on vähemalt üht võtit, OpenAI või Google Gemini. Kumbki üksi annab töötava äpi. OpenAI võti: Whisper transkriptsioon + GPT-4o analüüs ja katteplaanid. Gemini võti: parem eestikeelne transkriptsioon koos kõneleja-siltidega + Gemini analüüs ja katteplaanid. Mõlemad võtmed korraga avavad Hübriid-režiimi (Whisperi täpne ajastus + Gemini tekst). Seadistamine võtab ~5 minutit." },
      { q: "Kuidas äpp installitakse?", a: "Lae äpp alla App Store'ist." },
      { q: "Mis juhtub kui tühistan tellimuse?", a: "Kaotad ligipääsu äpile tellimuse lõppedes. Kõik salvestatud transkriptid jäävad telefoni." },
    ],
    mockInfo: "Intervjuu info",
    mockLocation: "Asukoht",
    mockLocationPh: "nt. Tallinna vanalinn",
    mockTopic: "Teema",
    mockTopicPh: "nt. kliimamuutused",
    mockInterviewer: "Intervjueerija",
    mockInterviewee: "Intervjueeritav",
    mockNamePh: "nimi",
    mockOptional: "(valikuline)",
    mockAddInterviewee: "+ Lisa intervjueeritav",
    mockEnvironment: "Võtte keskkond",
    mockAddPhoto: "Lisa foto",
    mockStartRec: "Alusta salvestust",
    mockTabNew: "Uus",
    mockTabHistory: "Ajalugu",
  },
  en: {
    nav: ["Features", "Workflow", "Pricing", "Contact"],
    navPaths: ["/features", "/workflow", "/pricing", "/contact"],
    badge: "Documentary · Field work",
    heroTitle: "Interview ends.\nB-roll begins.",
    heroSub: "DOKUField turns every interview into an immediate action plan — transcription, analysis and filmable shots right on location.",
    ctaPrimary: "Get the app",
    ctaSub: "iPhone & iPad · API key required",
    featTitle: "Everything you need on location",
    featSub: "DOKUField is built for videographers and content creators who want to spend more time on creativity, less on administration.",
    features: [
      { title: "Recording & transcript", desc: "Hit REC — DOKUField handles the rest. Your interview is transcribed automatically in Estonian and English, and the .m4a audio is saved to your phone for playback in Review.", icon: Mic },
      { title: "Reliable recording", desc: "Your recording and transcript won't be lost even if something goes wrong. In the field you can trust your material stays safe.", icon: ShieldCheck },
      { title: "B-roll AI", desc: "AI generates specific filmable shots across 4 categories — ON LOCATION, NEARBY, SYMBOLIC, WIDE SHOT.", icon: Layers },
      { title: "AI quick analysis", desc: "Top 3 quotes, cold open moment and emotional peak — right after the interview.", icon: FileText },
      { title: "Location photos", desc: "Add photos of the set — AI sees the space and suggests precise visuals.", icon: Camera },
      { title: "Export menu", desc: "Three buttons replaced by one. Choose transcript, analysis, audio file, or everything — single AirDrop session.", icon: Share2 },
      { title: "Privacy & control", desc: "Audio is sent to an AI service for transcription. Your transcript stays on your device.", icon: Zap },
      { title: "External mics work automatically", desc: "Plug in a Bluetooth mic or lavalier — DOKUField detects it and shows which input is active.", icon: Mic2 },
      { title: "Real-time audio meter", desc: "See the level while recording (green / yellow / red). Know instantly whether the mic is picking up and whether you're clipping.", icon: Activity },
      { title: "7-day free trial", desc: "Try B-roll suggestions and analysis for free. Cancel anytime in Apple ID settings. After trial: €19.99/month or €149.99/year.", icon: Sparkles },
    ],
    workflowTitle: "Four steps on location",
    workflowSub: "The app is designed so you can use it even when in a hurry — every step takes minimal time.",
    steps: [
      { n: "01", title: "Enter info", desc: "Location, topic, interviewee. Add photos of the set." },
      { n: "02", title: "Record", desc: "Press REC. Phone records — transcript is ready after recording." },
      { n: "03", title: "Get shots", desc: "AI analyzes and suggests filmable shots — while you're still there." },
      { n: "04", title: "Export transcript", desc: "Share .txt via AirDrop — transcript with metadata ready to use." },
    ],
    workflowDetailed: [
      {
        n: "01", title: "Enter interview info", time: "1 minute",
        simple: "Fill in the basics before recording.",
        what: "Open the app and fill in: Location, Topic, Interviewer, Interviewee. Optionally add up to 3 photos of the location — AI uses these later for more precise B-roll suggestions.",
        screen: "You see a simple form. All fields are optional except location.",
        tip: "Add a longer description in the Topic field — the more context, the more precise the B-roll suggestions.",
      },
      {
        n: "02", title: "Record the interview", time: "Interview length + ~2 min processing",
        simple: "Press REC and start the interview.",
        what: "The phone records audio. You can pause and resume. When you press Stop, the app automatically sends the audio file to Whisper AI for processing.",
        screen: "You see a running timer and a blinking red dot. After recording, Whisper's processing time is shown — the transcript appears when Whisper finishes.",
        tip: "The phone uses its built-in microphone only for transcription — your main audio records to a separate device as usual.",
      },
      {
        n: "03", title: "Review and generate B-roll", time: "~30 seconds",
        simple: "Whisper is done — read the transcript and generate B-roll suggestions.",
        what: "Whisper returns an accurate final transcript. Press 'Generate B-roll' — AI reads the transcript and suggests specific filmable shots across 4 categories: ON LOCATION, NEARBY, SYMBOLIC, WIDE SHOT.",
        screen: "You see the Whisper transcript with word count. After generating, the B-roll screen opens with each idea on a separate card. Tap a card to mark it as filmed.",
        tip: "Optionally generate a Quick Analysis — top 3 quotes, cold open moment and emotional peak.",
      },
      {
        n: "04", title: "Export the transcript", time: "10 seconds",
        simple: "Share the transcript via AirDrop — everything is ready to use.",
        what: "Press 'Share .txt' and AirDrop to Mac. The file automatically includes a metadata header (location, topic, interviewee, date) and the full transcript. You can also share an HTML version that opens in Safari as an interactive page.",
        screen: "iOS share sheet. Choose AirDrop, then Mac — file appears in Downloads immediately.",
        tip: "The transcript always stays in the History tab. You can return and mark B-roll suggestions later.",
      },
    ],
    testimonialTitle: "From filmmakers for filmmakers",
    testimonials: [
      { quote: "I used to lose half my B-roll ideas before I had time to write them down. Now everything is in the phone immediately.", name: "Hannes Raimla", role: "Videographer" },
      { quote: "The transcription handles Estonian speech surprisingly well. The transcript is ready before I pack up the camera.", name: "Mihkel", role: "Camera operator" },
    ],
    pricingTitle: "Simple pricing",
    pricingDesc: "One subscription. Your own API keys. Full cost control.",
    plans: [
      { name: "Solo", price: "€19.99", period: "/ month", yearPrice: "€149.99 / year", yearSaving: "Save 2 months", desc: "DOKUField with all features", features: ["Unlimited interviews", "B-roll + quick analysis", "Location photo analysis", "Transcript export (.txt)", "iPhone + iPad app"], cta: "Start free trial", highlight: false },
    ],
    apiExplain: {
      title: "What are API costs?",
      desc: "DOKUField uses two AI services — each one covers both transcription and analysis. You pay directly to the provider, not us.",
      whyTwo: "Why two services? Each one covers both transcription and analysis — pick one, or use both in Hybrid mode (Whisper's precise timing + Gemini's text).",
      services: [
        { name: "OpenAI", desc: "Transcription (Whisper) + analysis and B-roll (GPT-4o). Supports Estonian. Standalone key — enough on its own.", cost: "~€0.006 transcription (1h) + GPT-4o analysis", link: "https://platform.openai.com", linkText: "Create OpenAI account →" },
        { name: "Google Gemini", desc: "Better Estonian transcription with speaker labels + analysis and B-roll. Standalone key — enough on its own.", cost: "~€0.02–0.05 / interview", link: "https://ai.google.dev", linkText: "Create Google AI account →" },
      ],
      total: "In Hybrid mode, a maximum of ~€0.06 for a one-hour interview (Whisper + Gemini). With a single key the cost is lower. Shorter interviews cost less — Whisper bills by audio length.",
    },
    ctaTitle: "Start today.",
    ctaDesc: "Available on the App Store.",
    ctaBtn: "Get DOKUField",
    footer: "DOKUField · Field tool for videographers and content creators",
    contactTitle: "Contact",
    contactDesc: "Questions, feedback or want to try the app? Write to us.",
    contactEmail: "info@dokufield.app",
    faqTitle: "Frequently asked questions",
    faqs: [
      { q: "Does the app work offline?", a: "Recording works offline. AI transcription and B-roll generation require an internet connection." },
      { q: "Does it work on Android?", a: "Currently iOS only. Android version is planned." },
      { q: "Does transcription work in Estonian?", a: "Yes — in both Estonian and English. Whisper supports both and delivers accurate results." },
      { q: "Do I need to manage API keys myself?", a: "Yes — you need at least one key, either OpenAI or Google Gemini. Either one alone gives you a working app. OpenAI key: Whisper transcription + GPT-4o analysis and B-roll. Gemini key: better Estonian transcription with speaker labels + Gemini analysis and B-roll. Adding both unlocks Hybrid mode (Whisper's precise timing + Gemini's text). Setup takes ~5 minutes." },
      { q: "How is the app installed?", a: "Download the app from the App Store." },
      { q: "What happens if I cancel?", a: "You lose access to the app when the subscription ends. All saved transcripts remain on your phone." },
    ],
    mockInfo: "Interview info",
    mockLocation: "Location",
    mockLocationPh: "e.g. Tallinn Old Town",
    mockTopic: "Topic",
    mockTopicPh: "e.g. climate change",
    mockInterviewer: "Interviewer",
    mockInterviewee: "Interviewee",
    mockNamePh: "name",
    mockOptional: "(optional)",
    mockAddInterviewee: "+ Add interviewee",
    mockEnvironment: "Shooting environment",
    mockAddPhoto: "Add photo",
    mockStartRec: "Start recording",
    mockTabNew: "New",
    mockTabHistory: "History",
  }
};

function useReveal() {
  const [visible, setVisible] = useState({});
  const refs = useRef({});
  useEffect(() => {
    const obs = new IntersectionObserver((entries) => {
      entries.forEach(e => { if (e.isIntersecting) setVisible(v => ({ ...v, [e.target.dataset.id]: true })); });
    }, { threshold: 0.1 });
    Object.values(refs.current).forEach(r => r && obs.observe(r));
    return () => obs.disconnect();
  }, []);
  const setRef = (id) => (el) => { refs.current[id] = el; };
  const reveal = (id, delay = 0) => ({ "data-id": id, ref: setRef(id), style: { opacity: visible[id] ? 1 : 0, transform: visible[id] ? "translateY(0)" : "translateY(20px)", transition: `opacity 0.6s ease ${delay}s, transform 0.6s ease ${delay}s` } });
  return reveal;
}

function Nav({ lang, setLang }) {
  const t = translations[lang];
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => { setMenuOpen(false); }, [location.pathname]);

  return (
    <nav style={{ padding: "18px 40px", display: "flex", alignItems: "center", justifyContent: "space-between", background: "rgba(250,250,249,0.95)", backdropFilter: "blur(12px)", borderBottom: "1px solid #e8ede9", position: "sticky", top: 0, zIndex: 100 }}>
      <Link to="/" style={{ display: "flex", alignItems: "center", gap: 10, textDecoration: "none" }}>
        <img src="/dokufield_icon.png" alt="DOKUField" style={{ height: 40, width: "auto", display: "block" }}/>
        <span style={{ fontWeight: 600, fontSize: 18, color: "#111" }}>DOKUField</span>
      </Link>
      <div className="nav-links" style={{ display: "flex", gap: 28 }}>
        {t.nav.map((n, i) => (
          <Link key={n} to={t.navPaths[i]} style={{ color: location.pathname === t.navPaths[i] ? SAGE : "#666", fontSize: 14, textDecoration: "none", fontWeight: location.pathname === t.navPaths[i] ? 600 : 400, transition: "color 0.2s" }}>{n}</Link>
        ))}
      </div>
      <div className="nav-lang" style={{ display: "flex", gap: 8 }}>
        {["et","en"].map(l => (
          <button key={l} onClick={() => setLang(l)} style={{ padding: "6px 16px", borderRadius: 20, fontSize: 13, cursor: "pointer", border: "1px solid #dde3dd", background: lang===l ? SAGE : "transparent", color: lang===l ? "white" : "#666", transition: "all 0.2s", fontFamily: "inherit" }}>{l.toUpperCase()}</button>
        ))}
      </div>
      <button className="nav-hamburger" onClick={() => setMenuOpen(v => !v)} aria-label="Menüü">
        {menuOpen ? "✕" : "☰"}
      </button>
      {menuOpen && (
        <div className="nav-dropdown">
          {t.nav.map((n, i) => (
            <Link key={n} to={t.navPaths[i]} className="nav-dropdown-link" onClick={() => setMenuOpen(false)}>{n}</Link>
          ))}
          <div className="nav-dropdown-lang">
            {["et","en"].map(l => (
              <button key={l} onClick={() => { setLang(l); setMenuOpen(false); }} style={{ padding: "6px 16px", borderRadius: 20, fontSize: 13, cursor: "pointer", border: "1px solid #dde3dd", background: lang===l ? SAGE : "transparent", color: lang===l ? "white" : "#666", transition: "all 0.2s", fontFamily: "inherit" }}>{l.toUpperCase()}</button>
            ))}
          </div>
        </div>
      )}
    </nav>
  );
}

function Footer({ lang }) {
  const t = translations[lang];
  return (
    <footer style={{ padding: "32px 40px", background: "#111", textAlign: "center" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 10, marginBottom: 12 }}>
        <img src="/dokufield_icon.png" alt="DOKUField" style={{ height: 40, width: "auto", display: "block" }}/><span style={{ color: "white", fontWeight: 600, fontSize: 15 }}>DOKUField</span>
      </div>
      <p style={{ fontSize: 13, color: "rgba(255,255,255,0.4)", marginBottom: 8 }}>{t.footer}</p>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 16, flexWrap: "wrap" }}>
        <Link to="/privacy" style={{ fontSize: 12, color: "rgba(255,255,255,0.3)", textDecoration: "none", borderBottom: "1px solid rgba(255,255,255,0.15)", paddingBottom: 1, transition: "color 0.2s" }}
          onMouseEnter={e => e.target.style.color="rgba(255,255,255,0.6)"}
          onMouseLeave={e => e.target.style.color="rgba(255,255,255,0.3)"}
        >{lang === "et" ? "Privaatsuspoliitika" : "Privacy Policy"}</Link>
        <a href="https://www.instagram.com/pillelihannes" target="_blank" rel="noopener noreferrer" style={{ color: "rgba(255,255,255,0.3)", transition: "color 0.2s", lineHeight: 0 }}
          onMouseEnter={e => e.currentTarget.style.color="rgba(255,255,255,0.6)"}
          onMouseLeave={e => e.currentTarget.style.color="rgba(255,255,255,0.3)"}
        ><svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="2" width="20" height="20" rx="5" ry="5"/><circle cx="12" cy="12" r="4"/><circle cx="17.5" cy="6.5" r="1" fill="currentColor" stroke="none"/></svg></a>
        <a href="https://www.facebook.com/pillelihannes" target="_blank" rel="noopener noreferrer" style={{ color: "rgba(255,255,255,0.3)", transition: "color 0.2s", lineHeight: 0 }}
          onMouseEnter={e => e.currentTarget.style.color="rgba(255,255,255,0.6)"}
          onMouseLeave={e => e.currentTarget.style.color="rgba(255,255,255,0.3)"}
        ><svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"/></svg></a>
      </div>
    </footer>
  );
}

function CTASection({ lang }) {
  const t = translations[lang];
  return (
    <section className="cta-section" style={{ padding: "80px 40px", background: SAGE, textAlign: "center" }}>
      <h2 style={{ fontFamily: "'DM Serif Display', Georgia, serif", fontSize: 44, fontWeight: 400, marginBottom: 14, color: "white" }}>{t.ctaTitle}</h2>
      <p style={{ fontSize: 16, color: "rgba(255,255,255,0.8)", marginBottom: 32 }}>{t.ctaDesc}</p>
      <a href={APP_STORE_URL} target="_blank" rel="noopener noreferrer" style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "white", color: SAGE, fontWeight: 600, fontSize: 15, padding: "13px 32px", borderRadius: 12, border: "none", cursor: "pointer", fontFamily: "inherit", textDecoration: "none" }}>
        {t.ctaBtn} <ArrowRight size={15}/>
      </a>
    </section>
  );
}

function GlobalStyles() {
  return (
    <style>{`
      @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600&family=DM+Serif+Display&display=swap');
      * { box-sizing: border-box; margin: 0; padding: 0; }
      body { font-family: 'DM Sans', system-ui, sans-serif; background: #fafaf9; color: #1a1a1a; line-height: 1.6; }
      ::selection { background: ${SAGE}33; }
      @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.4; } }
      .feat-card { background: white; border: 1px solid #e8ede9; border-radius: 16px; padding: 24px; transition: transform 0.2s, box-shadow 0.2s; }
      .feat-card:hover { transform: translateY(-3px); box-shadow: 0 8px 32px rgba(119,137,121,0.12); }
      .nav-hamburger { display: none; }
      @media (max-width: 768px) {
        body, #root { overflow-x: hidden; }
        .nav-links { display: none !important; }
        .nav-lang { display: none !important; }
        .nav-hamburger { display: flex; align-items: center; justify-content: center; background: none; border: none; font-size: 22px; cursor: pointer; padding: 4px 8px; color: #333; line-height: 1; }
        .nav-dropdown { position: absolute; top: 100%; left: 0; right: 0; background: rgba(250,250,249,0.98); backdrop-filter: blur(12px); border-bottom: 1px solid #e8ede9; padding: 8px 24px 20px; display: flex; flex-direction: column; z-index: 99; box-shadow: 0 8px 24px rgba(0,0,0,0.08); }
        .nav-dropdown-link { color: #333; font-size: 16px; text-decoration: none; padding: 14px 0; border-bottom: 1px solid #f0eeeb; display: block; font-weight: 400; }
        .nav-dropdown-link:last-of-type { border-bottom: none; }
        .nav-dropdown-lang { display: flex; gap: 8px; padding-top: 16px; }
        .testimonials-section { width: 100%; box-sizing: border-box; }
        .cta-section { width: 100%; box-sizing: border-box; }
        .features-grid { grid-template-columns: 1fr !important; }
        .steps-grid { grid-template-columns: 1fr !important; }
        .workflow-mockup-grid { grid-template-columns: 1fr !important; width: 100%; }
        .workflow-step-cards { grid-template-columns: 1fr !important; width: 100%; }
        .pricing-grid { grid-template-columns: 1fr !important; }
        .api-grid { grid-template-columns: 1fr !important; }
        .hero-grid { grid-template-columns: 1fr !important; gap: 32px !important; }
        .hero-phone { width: 72vw !important; max-width: 280px !important; }
      }
    `}</style>
  );
}

// ─── ESILEHT ──────────────────────────────────────────────────────────────────
function HomePage({ lang }) {
  const t = translations[lang];
  const [testimonial, setTestimonial] = useState(0);
  const reveal = useReveal();
  const navigate = useNavigate();
  return (
    <div>
      {/* Hero */}
      <section className="hero-grid" style={{ padding: "100px 40px 80px", maxWidth: 960, margin: "0 auto", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 60, alignItems: "center" }}>
        <div>
          <div style={{ display: "inline-block", background: SAGE_LIGHT, color: SAGE_DARK, fontSize: 12, fontWeight: 600, padding: "5px 14px", borderRadius: 20, marginBottom: 24, letterSpacing: "0.05em", textTransform: "uppercase" }}>{t.badge}</div>
          <h1 style={{ fontFamily: "'DM Serif Display', Georgia, serif", fontSize: 52, fontWeight: 400, lineHeight: 1.15, marginBottom: 24, whiteSpace: "pre-line", color: "#111" }}>{t.heroTitle}</h1>
          <p style={{ fontSize: 17, color: "#555", marginBottom: 36, lineHeight: 1.7 }}>{t.heroSub}</p>
          <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
            <a href={APP_STORE_URL} target="_blank" rel="noopener noreferrer" style={{ display: "inline-flex", alignItems: "center", gap: 8, background: SAGE, color: "white", fontWeight: 500, fontSize: 15, padding: "13px 28px", borderRadius: 12, border: "none", cursor: "pointer", fontFamily: "inherit", textDecoration: "none" }}>
              {t.ctaPrimary} <ArrowRight size={15}/>
            </a>
            <span style={{ fontSize: 13, color: "#999" }}>{t.ctaSub}</span>
          </div>
        </div>
        {/* Telefoni mockup */}
        <div className="hero-phone-wrap" style={{ display: "flex", justifyContent: "center" }}>
          <div className="hero-phone" style={{ background: "#1a1a1a", borderRadius: 44, padding: "14px 10px 10px", width: 230, boxShadow: "0 40px 80px rgba(0,0,0,0.22)", border: "1px solid #333" }}>
            <div style={{ background: "#F2F2F7", borderRadius: 32, overflow: "hidden", height: 460, display: "flex", flexDirection: "column" }}>
              <div style={{ padding: "10px 16px 0", display: "flex", justifyContent: "space-between" }}>
                <span style={{ fontSize: 10, fontWeight: 600, color: "#111" }}>10:00</span>
              </div>
              <div style={{ padding: "8px 14px 6px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div style={{ fontSize: 16, fontWeight: 700, color: "#111" }}>DOKU <span style={{ color: SAGE }}>Field</span></div>
                <span style={{ fontSize: 16 }}>⚙️</span>
              </div>
              <div style={{ padding: "0 10px", flex: 1 }}>
                <div style={{ fontSize: 8, fontWeight: 600, color: "#8E8E93", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 3 }}>{t.mockInfo}</div>
                <div style={{ background: "white", borderRadius: 8, marginBottom: 8, overflow: "hidden" }}>
                  {[{l:t.mockLocation,p:t.mockLocationPh},{l:t.mockTopic,p:t.mockTopicPh,multi:true},{l:t.mockInterviewer,p:t.mockNamePh,sub:true},{l:t.mockInterviewee,p:t.mockNamePh,sub:true}].map((f,i)=>(
                    <div key={i} style={{padding:f.multi?"6px 8px 10px":"5px 8px",borderBottom:i<3?"0.5px solid #F0F0F0":"none",display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
                      <div><div style={{fontSize:9,fontWeight:500,color:"#111"}}>{f.l}</div>{f.sub&&<div style={{fontSize:7,color:"#8E8E93"}}>{t.mockOptional}</div>}</div>
                      <span style={{fontSize:8,color:"#C7C7CC"}}>{f.p}</span>
                    </div>
                  ))}
                  <div style={{padding:"5px 8px"}}><span style={{fontSize:8,color:SAGE,fontWeight:500}}>{t.mockAddInterviewee}</span></div>
                </div>
                <div style={{fontSize:8,fontWeight:600,color:"#8E8E93",textTransform:"uppercase",letterSpacing:"0.05em",marginBottom:3}}>{t.mockEnvironment}</div>
                <div style={{background:"white",borderRadius:8,padding:8,marginBottom:8}}>
                  <div style={{width:40,height:40,border:"1.5px dashed #C7C7CC",borderRadius:6,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center"}}>
                    <span style={{fontSize:14,color:"#C7C7CC",lineHeight:1}}>+</span>
                    <span style={{fontSize:6,color:"#C7C7CC"}}>{t.mockAddPhoto}</span>
                  </div>
                </div>
                <div style={{background:SAGE,borderRadius:10,padding:"9px",textAlign:"center"}}>
                  <span style={{color:"white",fontSize:10,fontWeight:600}}>{t.mockStartRec}</span>
                </div>
              </div>
              <div style={{background:"rgba(249,249,249,0.95)",borderTop:"0.5px solid #E5E5EA",padding:"6px 0 8px",display:"flex"}}>
                {[{icon:"🎙",label:t.mockTabNew,active:true},{icon:"🕐",label:t.mockTabHistory,active:false}].map((tab,i)=>(
                  <div key={i} style={{flex:1,textAlign:"center"}}>
                    <div style={{fontSize:14}}>{tab.icon}</div>
                    <div style={{fontSize:8,color:tab.active?SAGE:"#8E8E93",fontWeight:tab.active?600:400}}>{tab.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Funktsioonide lühiülevaade */}
      <section style={{ padding: "64px 40px", background: "#f5f7f5", borderTop: "1px solid #e8ede9" }}>
        <div style={{ maxWidth: 960, margin: "0 auto" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: 36 }}>
            <h2 {...reveal("feat",0)} style={{ fontFamily: "'DM Serif Display', Georgia, serif", fontSize: 34, fontWeight: 400, color: "#111" }}>{t.featTitle}</h2>
            <button onClick={() => navigate(t.navPaths[0])} style={{ fontSize: 14, color: SAGE, fontWeight: 600, background: "none", border: "none", cursor: "pointer", fontFamily: "inherit", display: "flex", alignItems: "center", gap: 4 }}>
              {lang==="et"?"Kõik funktsioonid":"All features"} <ArrowRight size={14}/>
            </button>
          </div>
          <div className="features-grid" style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 16 }}>
            {t.features.map((f,i) => (
              <div key={i} {...reveal(`feat${i}`,i*0.07)} className="feat-card">
                <div style={{width:36,height:36,borderRadius:9,background:SAGE_LIGHT,display:"flex",alignItems:"center",justifyContent:"center",marginBottom:14}}>
                  <f.icon size={17} color={SAGE}/>
                </div>
                <h3 style={{fontSize:14,fontWeight:600,marginBottom:6,color:"#111"}}>{f.title}</h3>
                <p style={{fontSize:13,color:"#666",lineHeight:1.6}}>{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Töövoo lühiülevaade */}
      <section style={{ padding: "64px 40px" }}>
        <div style={{ maxWidth: 960, margin: "0 auto" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: 36 }}>
            <h2 {...reveal("wf",0)} style={{ fontFamily: "'DM Serif Display', Georgia, serif", fontSize: 34, fontWeight: 400, color: "#111" }}>{t.workflowTitle}</h2>
            <button onClick={() => navigate(t.navPaths[1])} style={{ fontSize: 14, color: SAGE, fontWeight: 600, background: "none", border: "none", cursor: "pointer", fontFamily: "inherit", display: "flex", alignItems: "center", gap: 4 }}>
              {lang==="et"?"Täielik juhend":"Full guide"} <ArrowRight size={14}/>
            </button>
          </div>
          <div className="steps-grid" style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 20 }}>
            {t.steps.map((s,i) => (
              <div key={i} {...reveal(`step${i}`,i*0.1)} style={{borderTop:`2px solid ${SAGE}`,paddingTop:18}}>
                <div style={{fontSize:11,fontWeight:700,color:SAGE,letterSpacing:"0.1em",marginBottom:8}}>{s.n}</div>
                <h3 style={{fontSize:15,fontWeight:600,marginBottom:6,color:"#111"}}>{s.title}</h3>
                <p style={{fontSize:13,color:"#666",lineHeight:1.6}}>{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonialid */}
      <section className="testimonials-section" style={{ padding: "64px 40px", background: "#111" }}>
        <div style={{ maxWidth: 700, margin: "0 auto", textAlign: "center" }}>
          <h2 style={{ fontFamily: "'DM Serif Display', Georgia, serif", fontSize: 32, fontWeight: 400, marginBottom: 40, color: "white" }}>{t.testimonialTitle}</h2>
          <div style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 20, padding: "36px 44px", marginBottom: 20 }}>
            <p style={{ fontSize: 17, lineHeight: 1.7, color: "rgba(255,255,255,0.85)", marginBottom: 24, fontStyle: "italic" }}>"{t.testimonials[testimonial].quote}"</p>
            <div style={{ fontSize: 13, fontWeight: 600, color: SAGE }}>{t.testimonials[testimonial].name}</div>
            <div style={{ fontSize: 12, color: "rgba(255,255,255,0.4)", marginTop: 3 }}>{t.testimonials[testimonial].role}</div>
          </div>
          <div style={{ display: "flex", justifyContent: "center", gap: 10 }}>
            <button onClick={() => setTestimonial(v => Math.max(0,v-1))} style={{width:32,height:32,borderRadius:"50%",border:"1px solid rgba(255,255,255,0.2)",background:"transparent",color:"white",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center"}}><ChevronLeft size={14}/></button>
            {t.testimonials.map((_,i) => <div key={i} onClick={() => setTestimonial(i)} style={{width:7,height:7,borderRadius:"50%",background:i===testimonial?SAGE:"rgba(255,255,255,0.2)",cursor:"pointer",marginTop:12,transition:"background 0.2s"}}/>)}
            <button onClick={() => setTestimonial(v => Math.min(t.testimonials.length-1,v+1))} style={{width:32,height:32,borderRadius:"50%",border:"1px solid rgba(255,255,255,0.2)",background:"transparent",color:"white",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center"}}><ChevronRight size={14}/></button>
          </div>
        </div>
      </section>

      <CTASection lang={lang}/>
      <Footer lang={lang}/>
    </div>
  );
}

// ─── FUNKTSIOONIDE LEHT ───────────────────────────────────────────────────────
function FeaturesPage({ lang }) {
  const t = translations[lang];
  const reveal = useReveal();
  return (
    <div>
      <section style={{ padding: "80px 40px", maxWidth: 960, margin: "0 auto" }}>
        <div {...reveal("fh",0)} style={{ marginBottom: 64 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: SAGE, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 12 }}>{lang==="et"?"Funktsioonid":"Features"}</div>
          <h1 style={{ fontFamily: "'DM Serif Display', Georgia, serif", fontSize: 48, fontWeight: 400, color: "#111", marginBottom: 16 }}>{t.featTitle}</h1>
          <p style={{ fontSize: 17, color: "#555", maxWidth: 560, lineHeight: 1.7 }}>{t.featSub}</p>
        </div>
        <div className="features-grid" style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 24 }}>
          {t.features.map((f,i) => (
            <div key={i} {...reveal(`fp${i}`,i*0.08)} style={{background:"white",border:"1px solid #e8ede9",borderRadius:20,padding:32}}>
              <div style={{width:44,height:44,borderRadius:11,background:SAGE_LIGHT,display:"flex",alignItems:"center",justifyContent:"center",marginBottom:20}}>
                <f.icon size={20} color={SAGE}/>
              </div>
              <h3 style={{fontSize:17,fontWeight:600,marginBottom:10,color:"#111"}}>{f.title}</h3>
              <p style={{fontSize:15,color:"#555",lineHeight:1.7}}>{f.desc}</p>
            </div>
          ))}
        </div>
      </section>
      <CTASection lang={lang}/>
      <Footer lang={lang}/>
    </div>
  );
}

// ─── TÖÖVOO LEHT ──────────────────────────────────────────────────────────────
function WorkflowPage({ lang }) {
  const t = translations[lang];
  const reveal = useReveal();
  return (
    <div>
      <section style={{ padding: "80px 40px", maxWidth: 960, margin: "0 auto" }}>
        <div {...reveal("wh",0)} style={{ marginBottom: 56 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: SAGE, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 12 }}>{lang==="et"?"Töövoog":"Workflow"}</div>
          <h1 style={{ fontFamily: "'DM Serif Display', Georgia, serif", fontSize: 48, fontWeight: 400, color: "#111", marginBottom: 16 }}>{t.workflowTitle}</h1>
          <p style={{ fontSize: 17, color: "#555", maxWidth: 560, lineHeight: 1.7 }}>{t.workflowSub}</p>
        </div>

        {/* Äpi UI eelvaade */}
        <div {...reveal("wf-ui",0.1)} style={{ background: SAGE_LIGHT, borderRadius: 20, padding: "36px 44px", marginBottom: 72, border: `1px solid ${SAGE}33` }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: SAGE_DARK, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 20 }}>DOKUField — {lang==="et"?"äpi väljavõte":"app preview"}</div>
          <div className="workflow-mockup-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
            <div style={{ background: "white", borderRadius: 14, padding: 24, border: "1px solid #e8ede9" }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: "#8E8E93", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 4 }}>{lang==="et"?"Intervjuu info":"Interview info"}</div>
              <div style={{ fontSize: 12, color: "#aaa", marginBottom: 16 }}>{lang==="et"?"Täida põhiandmed enne salvestust":"Fill in the basics before recording"}</div>
              {[
                {l:lang==="et"?"Asukoht":"Location",p:lang==="et"?"nt. Tallinna vanalinn":"e.g. Old Town"},
                {l:lang==="et"?"Teema":"Topic",p:lang==="et"?"nt. kliimamuutused":"e.g. climate change",multi:true},
                {l:lang==="et"?"Intervjueerija":"Interviewer",p:lang==="et"?"nimi":"name",sub:true},
                {l:lang==="et"?"Intervjueeritav":"Interviewee",p:lang==="et"?"nimi":"name",sub:true}
              ].map((f,i) => (
                <div key={i} style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",padding:f.multi?"10px 0 16px":"8px 0",borderBottom:i<3?"0.5px solid #f0f0f0":"none"}}>
                  <div>
                    <div style={{fontSize:13,fontWeight:500,color:"#111"}}>{f.l}</div>
                    {f.sub&&<div style={{fontSize:11,color:"#aaa"}}>{lang==="et"?"(valikuline)":"(optional)"}</div>}
                  </div>
                  <span style={{fontSize:13,color:"#C7C7CC"}}>{f.p}</span>
                </div>
              ))}
              <div style={{paddingTop:10}}><span style={{fontSize:13,color:SAGE,fontWeight:500}}>+ {lang==="et"?"Lisa intervjueeritav":"Add interviewee"}</span></div>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              <div style={{ background: "white", borderRadius: 14, padding: 24, border: "1px solid #e8ede9" }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: "#8E8E93", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 4 }}>{lang==="et"?"Võtte keskkond":"Shoot environment"}</div>
                <div style={{ fontSize: 12, color: "#aaa", marginBottom: 14 }}>{lang==="et"?"Valikuline — AI analüüsib fotosid":"Optional — AI analyzes photos"}</div>
                <div style={{ display: "flex", gap: 10 }}>
                  {[1,2,3].map(n => (
                    <div key={n} style={{width:56,height:56,border:"1.5px dashed #C7C7CC",borderRadius:10,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:3}}>
                      <span style={{fontSize:18,color:"#C7C7CC",lineHeight:1}}>+</span>
                      <span style={{fontSize:8,color:"#C7C7CC"}}>{lang==="et"?"Lisa foto":"Add photo"}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div style={{ background: SAGE, borderRadius: 14, padding: "16px 20px", textAlign: "center" }}>
                <div style={{ color: "white", fontSize: 14, fontWeight: 600 }}>{lang==="et"?"Alusta salvestust":"Start recording"}</div>
                <div style={{ color: "rgba(255,255,255,0.6)", fontSize: 12, marginTop: 3 }}>{lang==="et"?"Vajuta REC ekraanil":"Tap REC on screen"}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Detailsed sammud */}
        {t.workflowDetailed.map((s,i) => (
          <div key={i} {...reveal(`wfd${i}`,i*0.08)} style={{display:"grid",gridTemplateColumns:"80px 1fr",gap:32,paddingBottom:56,borderBottom:i<t.workflowDetailed.length-1?"1px solid #e8ede9":"none",marginBottom:i<t.workflowDetailed.length-1?56:0}}>
            <div>
              <div style={{fontSize:26,fontWeight:700,color:SAGE,lineHeight:1,marginBottom:6}}>{s.n}</div>
              <div style={{fontSize:11,color:"#aaa",lineHeight:1.4}}>{s.time}</div>
            </div>
            <div>
              <h3 style={{fontSize:22,fontWeight:600,marginBottom:6,color:"#111"}}>{s.title}</h3>
              <p style={{fontSize:16,fontWeight:500,color:SAGE,marginBottom:20}}>{s.simple}</p>
              <div className="workflow-step-cards" style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14,marginBottom:14}}>
                {[{label:lang==="et"?"Mida teed":"What you do",text:s.what},{label:lang==="et"?"Mida näed ekraanil":"What you see",text:s.screen}].map((c,j)=>(
                  <div key={j} style={{background:"#fafaf9",border:"1px solid #e8ede9",borderRadius:12,padding:20}}>
                    <div style={{fontSize:10,fontWeight:700,color:"#aaa",textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:8}}>{c.label}</div>
                    <p style={{fontSize:14,color:"#444",lineHeight:1.7,margin:0}}>{c.text}</p>
                  </div>
                ))}
              </div>
              <div style={{display:"flex",alignItems:"flex-start",gap:10,background:SAGE_LIGHT,borderRadius:10,padding:"12px 16px"}}>
                <span style={{fontSize:14,flexShrink:0,marginTop:1}}>💡</span>
                <p style={{fontSize:13.5,color:SAGE_DARK,lineHeight:1.6,margin:0}}>{s.tip}</p>
              </div>
            </div>
          </div>
        ))}
      </section>
      <CTASection lang={lang}/>
      <Footer lang={lang}/>
    </div>
  );
}

// ─── HINNASTUSE LEHT ──────────────────────────────────────────────────────────
function PricingPage({ lang }) {
  const t = translations[lang];
  const reveal = useReveal();
  return (
    <div>
      <section style={{ padding: "80px 40px", maxWidth: 800, margin: "0 auto" }}>
        <div {...reveal("ph",0)} style={{ marginBottom: 56 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: SAGE, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 12 }}>{lang==="et"?"Hinnastus":"Pricing"}</div>
          <h1 style={{ fontFamily: "'DM Serif Display', Georgia, serif", fontSize: 48, fontWeight: 400, color: "#111", marginBottom: 16 }}>{t.pricingTitle}</h1>
          <p style={{ fontSize: 17, color: "#555", lineHeight: 1.7 }}>{t.pricingDesc}</p>
        </div>
        <div className="pricing-grid" style={{ display: "grid", gridTemplateColumns: "1fr", gap: 24, marginBottom: 48, maxWidth: 400, marginLeft: "auto", marginRight: "auto" }}>
          {t.plans.map((plan,i) => (
            <div key={i} style={{background:plan.highlight?"#111":"white",border:plan.highlight?"none":"1px solid #e8ede9",borderRadius:20,padding:32,position:"relative",opacity:plan.highlight?0.7:1}}>
              {plan.badge&&<div style={{position:"absolute",top:20,right:20,background:SAGE,color:"white",fontSize:11,fontWeight:600,padding:"4px 12px",borderRadius:20}}>{plan.badge}</div>}
              <div style={{fontSize:13,fontWeight:600,color:plan.highlight?"rgba(255,255,255,0.5)":"#888",marginBottom:8,textTransform:"uppercase",letterSpacing:"0.05em"}}>{plan.name}</div>
              <div style={{display:"flex",alignItems:"baseline",gap:4,marginBottom:6}}>
                <span style={{fontSize:42,fontWeight:700,color:plan.highlight?"white":"#111"}}>{plan.price}</span>
                <span style={{fontSize:15,color:plan.highlight?"rgba(255,255,255,0.5)":"#888"}}>{plan.period}</span>
              </div>
              <div style={{fontSize:13,color:plan.highlight?"rgba(255,255,255,0.4)":"#888",marginBottom:4}}>{plan.yearPrice}</div>
              <div style={{fontSize:12,color:SAGE,fontWeight:600,marginBottom:20}}>{plan.yearSaving}</div>
              <p style={{fontSize:14,color:plan.highlight?"rgba(255,255,255,0.6)":"#666",marginBottom:24,lineHeight:1.5}}>{plan.desc}</p>
              <div style={{marginBottom:28}}>
                {plan.features.map((f,j) => (
                  <div key={j} style={{display:"flex",alignItems:"center",gap:8,marginBottom:10}}>
                    <div style={{width:16,height:16,borderRadius:"50%",background:plan.highlight?"rgba(119,137,121,0.3)":SAGE_LIGHT,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                      <div style={{width:6,height:6,borderRadius:"50%",background:SAGE}}/>
                    </div>
                    <span style={{fontSize:13,color:plan.highlight?"rgba(255,255,255,0.7)":"#444"}}>{f}</span>
                  </div>
                ))}
              </div>
              <a href={APP_STORE_URL} target="_blank" rel="noopener noreferrer" style={{display:"block",boxSizing:"border-box",textAlign:"center",textDecoration:"none",width:"100%",padding:"12px",borderRadius:10,border:plan.highlight?"1px solid rgba(255,255,255,0.2)":`1px solid ${SAGE}`,background:plan.highlight?"rgba(255,255,255,0.05)":SAGE,color:plan.highlight?"rgba(255,255,255,0.5)":"white",fontSize:14,fontWeight:600,cursor:plan.highlight?"not-allowed":"pointer",fontFamily:"inherit"}}>{plan.cta}</a>
            </div>
          ))}
        </div>
        <div style={{ background: "white", border: "1px solid #e8ede9", borderRadius: 20, padding: 32 }}>
          <h3 style={{fontSize:20,fontWeight:600,marginBottom:8,color:"#111"}}>{t.apiExplain.title}</h3>
          <p style={{fontSize:15,color:"#555",lineHeight:1.7,marginBottom:16}}>{t.apiExplain.desc}</p>
          <div style={{background:SAGE_LIGHT,borderRadius:10,padding:"12px 16px",marginBottom:24,borderLeft:`3px solid ${SAGE}`}}>
            <p style={{fontSize:13.5,color:SAGE_DARK,lineHeight:1.7,margin:0}}>{t.apiExplain.whyTwo}</p>
          </div>
          <div className="api-grid" style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16,marginBottom:20}}>
            {t.apiExplain.services.map((s,i) => (
              <div key={i} style={{background:SAGE_LIGHT,borderRadius:12,padding:20}}>
                <div style={{fontSize:13,fontWeight:700,color:SAGE_DARK,marginBottom:6}}>{s.name}</div>
                <div style={{fontSize:13,color:"#555",lineHeight:1.6,marginBottom:10}}>{s.desc}</div>
                <div style={{fontSize:13,fontWeight:600,color:"#111",marginBottom:10}}>{s.cost}</div>
                <a href={s.link} target="_blank" rel="noopener noreferrer" style={{fontSize:13,color:SAGE,fontWeight:600,textDecoration:"none"}}>{s.linkText}</a>
              </div>
            ))}
          </div>
          <div style={{background:"#f5f7f5",borderRadius:10,padding:"14px 16px",fontSize:14,color:"#555",lineHeight:1.6}}>{t.apiExplain.total}</div>
        </div>
      </section>
      <CTASection lang={lang}/>
      <Footer lang={lang}/>
    </div>
  );
}

// ─── KONTAKT LEHT ─────────────────────────────────────────────────────────────
function ContactPage({ lang }) {
  const t = translations[lang];
  const reveal = useReveal();
  const [open, setOpen] = useState(null);
  return (
    <div>
      <section style={{ padding: "80px 40px", maxWidth: 800, margin: "0 auto" }}>
        <div {...reveal("ch",0)} style={{ marginBottom: 56 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: SAGE, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 12 }}>{lang==="et"?"Kontakt":"Contact"}</div>
          <h1 style={{ fontFamily: "'DM Serif Display', Georgia, serif", fontSize: 48, fontWeight: 400, color: "#111", marginBottom: 16 }}>{t.contactTitle}</h1>
          <p style={{ fontSize: 17, color: "#555", lineHeight: 1.7 }}>{t.contactDesc}</p>
        </div>
        <div {...reveal("ccreator",0.05)} className="creator-card" style={{background:"white",border:"1px solid #e8ede9",borderRadius:20,padding:32,marginBottom:32}}>
          <img src="/Profiil.jpeg" alt="Hannes Raimla" style={{width:88,height:88,borderRadius:"50%",objectFit:"cover",flexShrink:0}}/>
          <div>
            <div style={{fontSize:12,fontWeight:600,color:SAGE,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:8}}>{lang==="et"?"Looja":"Creator"}</div>
            <p style={{fontSize:15,color:"#555",lineHeight:1.7,margin:0}}>{lang==="et"
              ? <>Olen Hannes Raimla, videograaf ja dokumentaalfilmitegija Saaremaalt. Koos elukaaslase Pilleliga tegutseme loomemajanduses läbi brändi <a href="https://pillelihannes.ee" target="_blank" rel="noopener noreferrer" style={{color:SAGE,textDecoration:"none",fontWeight:600}}>Pilleli & Hannes</a>. Lõin DOKUField'i sest kaotasin liiga palju aega välitöödel käsitsi märkmete tegemisel. Nüüd kasutan seda ise igal võttel.</>
              : <>I'm Hannes Raimla, a videographer and documentary filmmaker from Saaremaa, Estonia. Together with my partner Pilleli, we run a creative studio called <a href="https://pillelihannes.ee" target="_blank" rel="noopener noreferrer" style={{color:SAGE,textDecoration:"none",fontWeight:600}}>Pilleli & Hannes</a>. I built DOKUField because I was losing too much time taking notes by hand in the field. Now I use it myself on every shoot.</>
            }</p>
          </div>
        </div>
        <div {...reveal("cemail",0.1)} style={{background:"white",border:"1px solid #e8ede9",borderRadius:20,padding:32,marginBottom:48,display:"flex",alignItems:"center",gap:20}}>
          <div style={{width:52,height:52,borderRadius:13,background:SAGE_LIGHT,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
            <Mail size={22} color={SAGE}/>
          </div>
          <div>
            <div style={{fontSize:13,color:"#888",marginBottom:4}}>{lang==="et"?"Kirjuta meile":"Write to us"}</div>
            <a href={`mailto:${t.contactEmail}`} style={{fontSize:18,fontWeight:600,color:SAGE,textDecoration:"none"}}>{t.contactEmail}</a>
          </div>
        </div>
        <div {...reveal("cfaq",0.15)}>
          <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:24}}>
            <HelpCircle size={20} color={SAGE}/>
            <h2 style={{fontFamily:"'DM Serif Display', Georgia, serif",fontSize:28,fontWeight:400,color:"#111"}}>{t.faqTitle}</h2>
          </div>
          <div style={{display:"flex",flexDirection:"column",gap:2}}>
            {t.faqs.map((faq,i) => (
              <div key={i} style={{background:"white",border:"1px solid #e8ede9",borderRadius:12,overflow:"hidden"}}>
                <button onClick={() => setOpen(open===i?null:i)} style={{width:"100%",padding:"18px 24px",display:"flex",justifyContent:"space-between",alignItems:"center",background:"none",border:"none",cursor:"pointer",fontFamily:"inherit",textAlign:"left"}}>
                  <span style={{fontSize:15,fontWeight:500,color:"#111"}}>{faq.q}</span>
                  <span style={{fontSize:18,color:SAGE,transform:open===i?"rotate(45deg)":"none",transition:"transform 0.2s",flexShrink:0,marginLeft:12}}>+</span>
                </button>
                {open===i&&<div style={{padding:"0 24px 18px",fontSize:14,color:"#555",lineHeight:1.7}}>{faq.a}</div>}
              </div>
            ))}
          </div>
        </div>
      </section>
      <Footer lang={lang}/>
    </div>
  );
}

// ─── PRIVAATSUSPOLIITIKA LEHT ─────────────────────────────────────────────────
function PrivacyPage({ lang }) {
  const reveal = useReveal();
  const sections = [
    {
      title: lang === "et" ? "Andmed mida kogume" : "Data We Collect",
      body: lang === "et"
        ? "DOKUField ei kogu, salvesta ega jaga ühtegi isikuandmeid meie serverites. Kõik andmed jäävad sinu seadmesse."
        : "DOKUField does not collect, store, or share any personal data on our servers. All data remains on your device.",
    },
    {
      title: lang === "et" ? "API võtmed" : "API Keys",
      body: lang === "et"
        ? "Sinu OpenAI ja Google Gemini API võtmed salvestatakse ainult iOS Keychain'i — turvalisse krüpteeritud salvestusruumi, millele väljaspool äppi ligipääsu ei ole."
        : "Your OpenAI and Google Gemini API keys are stored exclusively in iOS Keychain — a secure, encrypted storage that is inaccessible outside the app.",
    },
    {
      title: lang === "et" ? "Salvestused ja transkriptid" : "Interview Recordings and Transcripts",
      body: lang === "et"
        ? "Helisalvestised saadetakse transkriptsiooniks OpenAI-le (Whisper) ja/või Google'ile (Gemini), sõltuvalt valitud transkriptsioonirežiimist. Salvestised ja transkriptid hoitakse ainult sinu seadmes ning meie ei pääse neile ligi, ei salvesta ega jaga neid."
        : "Audio recordings are sent to OpenAI (Whisper) and/or Google (Gemini) for transcription, depending on the transcription mode you choose. Recordings and transcripts are stored on your device only and are never accessed, stored, or shared by us.",
    },
    {
      title: lang === "et" ? "Katteplaanid ja analüüs" : "B-roll Suggestions and Analysis",
      body: lang === "et"
        ? "Intervjuu transkriptid saadetakse Google Gemini API-le või OpenAI GPT-4o-le (kui Gemini võtit ei ole sisestatud) katteplaanide soovituste ja sisuanalüüsi genereerimiseks. Neid andmeid reguleerib vastava teenusepakkuja privaatsuspoliitika."
        : "Interview transcripts are sent to Google Gemini or OpenAI GPT-4o (if no Gemini key is provided) to generate B-roll suggestions and content analysis. This data is governed by the respective provider's privacy policy.",
    },
    {
      title: lang === "et" ? "Kolmanda osapoole teenused" : "Third Party Services",
      body: null,
      links: [
        { label: "OpenAI Whisper", href: "https://openai.com/privacy" },
        { label: "OpenAI GPT-4o", href: "https://openai.com/privacy" },
        { label: "Google Gemini", href: "https://ai.google.dev/terms" },
      ],
    },
    {
      title: lang === "et" ? "Kontakt" : "Contact",
      body: lang === "et"
        ? "Küsimused privaatsuse kohta? Võta meiega ühendust aadressil raimlahannes@gmail.com"
        : "Questions about privacy? Contact us at raimlahannes@gmail.com",
      email: "raimlahannes@gmail.com",
    },
  ];

  return (
    <div>
      <section style={{ padding: "80px 40px", maxWidth: 800, margin: "0 auto" }}>
        <div {...reveal("ph", 0)} style={{ marginBottom: 56 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: SAGE, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 12 }}>
            {lang === "et" ? "Privaatsuspoliitika" : "Privacy Policy"}
          </div>
          <h1 style={{ fontFamily: "'DM Serif Display', Georgia, serif", fontSize: 48, fontWeight: 400, color: "#111", marginBottom: 16 }}>
            {lang === "et" ? "Sinu andmed on sinu omad." : "Your data belongs to you."}
          </h1>
          <p style={{ fontSize: 15, color: "#888" }}>
            {lang === "et" ? "Viimati uuendatud: 17. juuli 2026" : "Last updated: July 17, 2026"}
          </p>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {sections.map((sec, i) => (
            <div key={i} {...reveal(`ps${i}`, i * 0.05)} style={{ background: "white", border: "1px solid #e8ede9", borderRadius: 16, padding: "28px 32px" }}>
              <h2 style={{ fontFamily: "'DM Serif Display', Georgia, serif", fontSize: 22, fontWeight: 400, color: "#111", marginBottom: 12 }}>{sec.title}</h2>
              {sec.body && !sec.email && (
                <p style={{ fontSize: 15, color: "#555", lineHeight: 1.75, margin: 0 }}>{sec.body}</p>
              )}
              {sec.email && (
                <p style={{ fontSize: 15, color: "#555", lineHeight: 1.75, margin: 0 }}>
                  {lang === "et" ? "Küsimused privaatsuse kohta? Võta meiega ühendust aadressil " : "Questions about privacy? Contact us at "}
                  <a href={`mailto:${sec.email}`} style={{ color: SAGE, textDecoration: "none", fontWeight: 500 }}>{sec.email}</a>
                </p>
              )}
              {sec.links && (
                <ul style={{ margin: 0, padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 8 }}>
                  {sec.links.map((l, j) => (
                    <li key={j} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <div style={{ width: 6, height: 6, borderRadius: "50%", background: SAGE, flexShrink: 0 }}/>
                      <span style={{ fontSize: 15, color: "#555" }}>{l.label} — </span>
                      <a href={l.href} target="_blank" rel="noopener noreferrer" style={{ fontSize: 15, color: SAGE, textDecoration: "none", fontWeight: 500 }}>{l.href.replace("https://", "")}</a>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </div>
      </section>
      <Footer lang={lang}/>
    </div>
  );
}

// ─── PEAKOMPONENT ─────────────────────────────────────────────────────────────
export default function App() {
  const [lang, setLang] = useState(navigator.language.startsWith("et") ? "et" : "en");
  return (
    <BrowserRouter>
      <GlobalStyles/>
      <Nav lang={lang} setLang={setLang}/>
      <Routes>
        <Route path="/" element={<HomePage lang={lang}/>}/>
        <Route path="/funktsioonid" element={<FeaturesPage lang={lang}/>}/>
        <Route path="/features" element={<FeaturesPage lang={lang}/>}/>
        <Route path="/toovoog" element={<WorkflowPage lang={lang}/>}/>
        <Route path="/workflow" element={<WorkflowPage lang={lang}/>}/>
        <Route path="/hinnastus" element={<PricingPage lang={lang}/>}/>
        <Route path="/pricing" element={<PricingPage lang={lang}/>}/>
        <Route path="/kontakt" element={<ContactPage lang={lang}/>}/>
        <Route path="/contact" element={<ContactPage lang={lang}/>}/>
        <Route path="/privacy" element={<PrivacyPage lang={lang}/>}/>
      </Routes>
    </BrowserRouter>
  );
}
